import SmallLoader from "../../common/SmallLoader";
import CentredLoader from "./CentredLoader";
import { CheckCircle, InfoOutlined } from "@mui/icons-material";
import { Alert, Box, Tab, Tabs, Tooltip } from "@mui/material";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import classnames from "classnames";
import SheetPreviewCell from "components/inputs/claims/SheetPreviewCell";
import { useEffect, useMemo, useState } from "react";
import Spreadsheet from "react-spreadsheet";
import * as tablesService from "services/tablesService";
import { useGetTableMetadataQuery } from "services/tablesService";
import { formatDate } from "utils";

const PageTab = ({ fileId, tableName, ...props }) => {
  const {
    data: tableMeta,
    status: tableMetaStatus,
  } = useGetTableMetadataQuery({ fileId, tableName, refresh: false });
  let tabLabel = "";
  let tabIcon = "";
  if (tableMetaStatus === "fulfilled") {
    if (tableMeta?.status === "LOADED") {
      tabLabel = `${tableName} (${tableMeta?.rows} rows)`;
    } else {
      tabLabel = `${tableName} - FAILED: ${
        tableMeta.message ? tableMeta.message : ""
      }`;
    }
  } else {
    tabLabel = `${tableName}`;
    tabIcon = <SmallLoader type={"TailSpin"} />;
  }
  return (
    <Tab
      {...props}
      label={tabLabel}
      icon={tabIcon}
      iconPosition={"start"}
      sx={{
        minHeight: "48px",
        fontSize: "13px",
        borderTopLeftRadius: "0.5rem",
        borderTopRightRadius: "0.5rem",
        borderRight: "0.5px solid gainsboro",
        background: "#f5f5f5",
        "&.Mui-selected": { background: "white !important" },
      }}
    />
  );
};

const truncatedValue = (str) => ({
  displayValue: str.length > 20 ? str.substring(0, 17) + "..." : str,
  tooltip: str.length > 20 ? str : undefined,
});

export const formatCellValue = (cell) => {
  if (cell == null) {
    return { value: "", type: "blank" };
  }
  const { value, type } = cell;
  const formattedValue = cell.styles?.formatted;
  if (formattedValue != null) {
    return {
      ...cell,
      ...truncatedValue(formattedValue),
    };
  }
  if (type === "str") {
    return {
      ...cell,
      ...truncatedValue(value),
    };
  }
  if (type === "date") {
    return {
      ...cell,
      displayValue: formatDate(new Date(value)),
    };
  }
  return cell;
};

// New multi-sheet selector component
const SheetCard = ({
  sheet,
  isSelected,
  onToggle,
  isRecommended,
  borderColor,
}) => {
  const confidence = sheet.claims_confidence || 0;
  const confidenceColor =
    confidence >= 0.8
      ? "success"
      : confidence >= 0.5
      ? "warning"
      : confidence >= 0.2
      ? "error"
      : "default";

  return (
    <Card
      sx={{
        cursor: "pointer",
        border: isSelected ? "2px solid #ff9800" : `1px solid ${borderColor}`,
        backgroundColor: isSelected ? "#fff3e0" : "white",
        transition: "all 0.2s ease-in-out",
        height: "200px", // Fixed height for consistent sizing
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: isSelected ? "#ff9800" : "#bdbdbd",
          backgroundColor: isSelected ? "#fff3e0" : "#fafafa",
        },
      }}
      onClick={() => onToggle(sheet.sheet_name)}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"flex-start"}
          sx={{ height: "100%" }}
        >
          <Stack flex={1} sx={{ minHeight: 0 }}>
            <Stack direction={"row"} alignItems={"center"} gap={1}>
              <Typography variant={"h6"} sx={{ fontSize: "1rem" }}>
                {sheet.sheet_name}
              </Typography>
              {isRecommended && (
                <Chip
                  icon={<CheckCircle />}
                  label={"Recommended"}
                  color={"primary"}
                  size={"small"}
                  sx={{
                    backgroundColor: "#ff9800",
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              )}
            </Stack>
            <Typography
              variant={"body2"}
              color={"text.secondary"}
              sx={{
                mt: 1,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {sheet.reasoning ||
                (confidence === 0
                  ? "No claims data detected"
                  : "Sheet analysis available")}
            </Typography>
            <Stack direction={"row"} gap={1} sx={{ mt: 2 }}>
              <Chip
                label={`${Math.round(confidence * 100)}% Claims Confidence`}
                color={confidenceColor}
                size={"small"}
                sx={{
                  fontWeight: confidence >= 0.8 ? "bold" : "normal",
                }}
              />
              <Chip
                label={`${sheet.column_mappings?.length || 0} Column Mappings`}
                size={"small"}
                variant={"outlined"}
                color={
                  sheet.column_mappings?.length > 0 ? "primary" : "default"
                }
                sx={{
                  ...(sheet.column_mappings?.length > 0 && {
                    borderColor: "#ff9800",
                    color: "#ff9800",
                  }),
                }}
              />
            </Stack>
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={isSelected}
                color={"primary"}
                sx={{
                  color: isSelected ? "#ff9800" : "#757575",
                  "&.Mui-checked": {
                    color: "#ff9800",
                  },
                }}
              />
            }
            label={""}
            sx={{ ml: 2 }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

const MultiSheetSelector = ({
  sheetAnalysisResults,
  selectedSheets,
  onSelectionChange,
}) => {
  const theme = useTheme();
  const gainsboro = theme.brand.gray.gainsboro;

  if (!sheetAnalysisResults?.sheets) {
    return (
      <Alert severity={"info"} sx={{ mt: 2 }}>
        {"No sheet analysis results available."}
      </Alert>
    );
  }

  const recommendedSheet = sheetAnalysisResults.recommended_sheet;

  // Show all sheets, ordered by recommended first, then by confidence
  const sheets = sheetAnalysisResults.sheets.toSorted((a, b) => {
    // Recommended sheet first
    if (a.sheet_name === recommendedSheet) return -1;
    if (b.sheet_name === recommendedSheet) return 1;

    // Then by claims confidence (highest first)
    return (b.claims_confidence || 0) - (a.claims_confidence || 0);
  });

  const handleSheetToggle = (sheetName) => {
    const newSelection = selectedSheets.includes(sheetName)
      ? selectedSheets.filter((s) => s !== sheetName)
      : [...selectedSheets, sheetName];
    onSelectionChange(newSelection);
  };

  const selectAll = () => {
    onSelectionChange(sheets.map((sheet) => sheet.sheet_name));
  };

  const clearAll = () => {
    onSelectionChange([]);
  };

  const selectRecommended = () => {
    if (recommendedSheet) {
      onSelectionChange([recommendedSheet]);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography variant={"h6"}>{"Select Sheets to Process"}</Typography>
        <Stack direction={"row"} gap={1}>
          <Button
            size={"medium"}
            onClick={selectRecommended}
            disabled={!recommendedSheet}
            variant={"contained"}
            color={"primary"}
            sx={{
              backgroundColor: "#ff9800",
              "&:hover": { backgroundColor: "#f57c00" },
              "&:disabled": { backgroundColor: gainsboro },
            }}
          >
            {"Select Recommended"}
          </Button>
          <Button
            size={"medium"}
            onClick={selectAll}
            variant={"outlined"}
            color={"primary"}
            sx={{
              borderColor: "#ff9800",
              color: "#ff9800",
              "&:hover": {
                borderColor: "#f57c00",
                backgroundColor: "rgba(255, 152, 0, 0.04)",
              },
            }}
          >
            {"Select All"}
          </Button>
          <Button
            size={"medium"}
            onClick={clearAll}
            disabled={selectedSheets.length === 0}
            variant={"outlined"}
            color={"secondary"}
            sx={{
              borderColor: "#757575",
              color: "#757575",
              "&:hover": {
                borderColor: "#616161",
                backgroundColor: "rgba(117, 117, 117, 0.04)",
              },
              "&:disabled": {
                borderColor: gainsboro,
                color: "#bdbdbd",
              },
            }}
          >
            {"Clear All"}
          </Button>
        </Stack>
      </Stack>

      {sheets.length === 0 ? (
        <Alert severity={"warning"}>
          {"No sheets were found in the analysis."}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {sheets.map((sheet) => (
            <Grid item xs={12} md={6} lg={4} key={sheet.sheet_name}>
              <SheetCard
                sheet={sheet}
                isSelected={selectedSheets.includes(sheet.sheet_name)}
                onToggle={handleSheetToggle}
                isRecommended={sheet.sheet_name === recommendedSheet}
                borderColor={gainsboro}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Divider />

      <Stack direction={"row"} alignItems={"center"} gap={1}>
        <InfoOutlined color={"action"} />
        <Typography variant={"body2"} color={"text.secondary"}>
          {`Selected ${selectedSheets.length} of ${sheets.length} sheets. Each sheet will be processed separately and can be configured independently.`}
        </Typography>
      </Stack>
    </Stack>
  );
};

// Original SheetSelector component
const SheetSelector = ({
  fileId,
  tableName,
  origin,
  setTableName,
  setOrigin,
  setTablesList,
}) => {
  const {
    data: sheetNames,
    status: sheetNamesStatus,
  } = tablesService.useGetTableListQuery(fileId, { skip: !fileId });
  const uniqueKey = `${fileId}:${tableName}`;

  const [tableCheckList, setTableCheckList] = useState(null);
  useEffect(() => {
    if (sheetNamesStatus === "fulfilled") {
      const sheetList = Object.entries(sheetNames.tables)
        .map(([sheet, { sheetIndex }]) => ({ sheet, sheetIndex }))
        .sort((a, b) => a.sheetIndex ?? 0 - b.sheetIndex ?? 0)
        .map((e) => e.sheet);
      if (
        JSON.stringify(tableCheckList || []) !== JSON.stringify(sheetList || [])
      ) {
        setTablesList(sheetList);
        setTableCheckList(sheetList);
      }
    }
  }, [sheetNames, sheetNamesStatus, setTablesList, tableCheckList]);
  const [tableMetaStable, setTableMetaStable] = useState({});
  const { data: tableMeta, status: tableMetaStatus } = useGetTableMetadataQuery(
    { fileId, tableName },
    {
      pollingInterval: tableMetaStable[uniqueKey] ? 0 : 1000,
      skip: !fileId || !tableName,
    }
  );
  useEffect(() => {
    if (tableMetaStatus === "fulfilled" && tableMeta.finalState) {
      setTableMetaStable((existing) => ({ ...existing, [uniqueKey]: true }));
    }
  }, [tableMetaStatus, tableMeta, setTableMetaStable, uniqueKey]);
  const layersLoaded = Object.values(tableMeta?.layers || {}).filter(
    (x) => x === "LOADED"
  ).length;
  const styledArgs = useMemo(
    () => ({ fileId, tableName, layersLoaded, maxColumns: 300 }),
    [fileId, tableName, layersLoaded]
  );

  const {
    data: page,
    status: pageStatus,
  } = tablesService.useGetStyledCellsPageQuery(styledArgs, {
    skip: !fileId || !tableName || tableMetaStatus !== "fulfilled",
  });
  const [pagesLoaded, setPagesLoaded] = useState({});
  useEffect(() => {
    if (pageStatus === "fulfilled") {
      setPagesLoaded((prev) =>
        prev[uniqueKey] ? prev : { ...prev, [uniqueKey]: true }
      );
    }
  }, [pageStatus, uniqueKey]);

  const tableNames = useMemo(
    () =>
      Object.entries(sheetNames?.tables ?? {})
        .sort(([, a], [, b]) => (a?.sheetIndex ?? 0) - (b?.sheetIndex ?? 0))
        .map(([tableName]) => tableName),
    [sheetNames]
  );

  const [tableIndex, setTableIndex] = useState(0);
  useEffect(() => {
    if ((tableNames?.length ?? 0) > 0) {
      const newIndex = (tableNames ?? []).indexOf(tableName);
      if (newIndex < 0) {
        setTableName(tableNames[0]);
        setTableIndex(0);
      } else {
        if (newIndex !== tableIndex) {
          setTableIndex(newIndex);
        }
      }
    }
  }, [tableName, tableNames, setTableIndex, setTableName, tableIndex]);

  useEffect(() => {
    if (tableName == null && tableNames?.length) {
      setTableName(tableNames[0]);
    }
  }, [tableName, tableNames, setTableName]);

  useEffect(() => {
    if (origin == null) {
      setOrigin({ row: 0, column: 0 });
    }
  }, [origin, setOrigin]);

  const stylesFromData = ({ data }) => {
    const { colour, fontColour } = data?.styles || {};
    return {
      ...(colour && { "background-color": `#${colour}` }),
      ...(fontColour && { color: `#${fontColour}` }),
    };
  };

  const HighlightedDataViewer = ({ row, column, data, rootRef }) => {
    const topRow = row === origin?.row;
    const pastTopRow = row > origin?.row;
    const topColumn = column === origin?.column;
    const pastTopColumn = column > origin?.column;

    const active = topRow && topColumn;
    const border = (topRow && pastTopColumn) || (topColumn && pastTopRow);
    const inside = pastTopRow && pastTopColumn;

    const style = stylesFromData({ data });

    const cell = (
      <td
        ref={rootRef}
        className={classnames(
          "sheet-preview-cell",
          active && "sheet-preview-cell-active",
          border && "sheet-preview-cell-border",
          inside && "sheet-preview-cell-content",
          active && "sheet-preview-cell-border-top-left",
          topRow && pastTopColumn && "sheet-preview-cell-border-top",
          topColumn && pastTopRow && "sheet-preview-cell-border-left",
          data?.type && `sheet-preview-cell-type-${data.type}`,
          data?.className
        )}
        style={style}
        tabIndex={0}
        onMouseDown={() => setOrigin({ row, column })}
      >
        <span style={{ padding: "2px 4px" }}>
          {data?.displayValue ?? data?.value ?? ""}
        </span>
      </td>
    );
    return data?.tooltip ? (
      <Tooltip title={data.tooltip}>{cell}</Tooltip>
    ) : (
      cell
    );
  };

  const hasData = pageStatus === "fulfilled" && page?.page?.length;
  const hasError = sheetNamesStatus === "rejected" || pageStatus === "rejected";

  if (!fileId) {
    return <></>;
  }

  if (
    sheetNamesStatus !== "fulfilled" ||
    tableName == null ||
    !pagesLoaded[uniqueKey]
  ) {
    return <CentredLoader label={"Preparing Preview"} />;
  }

  const emptyData = (rows, cols) => {
    const templateRow = Array.from({ length: cols }, () => ({ value: null }));
    return Array.from({ length: rows }, () => [...templateRow]);
  };

  return (
    <Box className={"fade-in-slow"}>
      <DialogContentText marginTop={1} marginBottom={1}>
        {
          "Please choose the top corner of the region you'd like to extract from the spreadsheet"
        }
      </DialogContentText>
      <Tabs
        value={tableIndex}
        variant={"scrollable"}
        onChange={(_, newTableIndex) =>
          setTableName(tableNames?.[newTableIndex])
        }
      >
        {tableNames.map((tableName) => (
          <PageTab key={tableName} tableName={tableName} fileId={fileId} />
        ))}
      </Tabs>
      {hasError ? (
        <Alert severity={"error"}>
          {"Sorry, there was an error reading this sheet"}
        </Alert>
      ) : pageStatus !== "fulfilled" ? (
        <Spreadsheet data={emptyData(10, 5)} />
      ) : hasData ? (
        <Spreadsheet
          className={"sheet-transparent-selection"}
          data={(page?.page ?? []).map((row) => row.map(formatCellValue))}
          Cell={SheetPreviewCell}
          DataViewer={HighlightedDataViewer}
        />
      ) : (
        <Alert severity={"info"}>
          {"This sheet doesn't appear to contain any data"}
        </Alert>
      )}
    </Box>
  );
};

export default SheetSelector;
export { MultiSheetSelector };
